import React, { useRef } from 'react';
import { Box, Button, Chip, Grid, Switch, Typography } from '@mui/material';
import { Edit, Inventory, LocationOn } from '@mui/icons-material';
import { isEmpty, forEach } from 'lodash';
import { colors } from '../../assets/utils/colors';
import { numberWithCommas } from '../../utils/helpers';
import UpdateBranch from '../../pages/branches/UpdateBranch';
import CustomCard2 from '../cards/CustomCard2';
import CustomDialogy from '../dialogys/CustomDialogy';

// import RTK query and redux
import {
    useActivateBranchMutation,
    useDeactivateBranchMutation,
} from '../../api/branches/branchAPI';

// !############### MAIN FUNC ####################
const BranchDetailsSidebar = ({ productsCount, outStockCount, branchInfo }) => {
    const [setBranchActive, result] = useActivateBranchMutation();
    const [setBranchInActive, result2] = useDeactivateBranchMutation();
    const [openDialogy, setOpenDialogy] = React.useState(false);
    const inputRef = useRef();
    const handleOpenDialogy = () => {
        setOpenDialogy(true);
    };

    const handleCloseDialogy = () => {
        setOpenDialogy(false);
    };

    let totalSales = 0;

    if (!isEmpty(branchInfo)) {
        const { branch_products } = branchInfo;

        forEach(
            branch_products,
            ({ product_sold, product_price }) =>
                (totalSales += product_sold * product_price),
        );
    }

    const activateBranch = (e) => {
        const value = inputRef.current.checked;
        if (!value) {
            setBranchActive(branchInfo.id);
        } else {
            setBranchInActive(branchInfo.id);
        }
    };

    if (result.isSuccess && inputRef.current.checked) {
        console.log('Branch Activated');
    }

    if (result2.isSuccess && !inputRef.current.checked) {
        console.log('Branch deactivated');
    }

    return (
        <>
            {/* ############### Dialogy ################# */}
            <CustomDialogy
                title={'Update branch details'}
                open={openDialogy}
                handleCloseDialogy={handleCloseDialogy}
            >
                <UpdateBranch />
            </CustomDialogy>

            {/* ################ Main Content ################ */}
            <CustomCard2
                title={'Branch details'}
                height={640}
                action={
                    <Button
                        variant="contained"
                        size="small"
                        className="app-btn"
                        startIcon={<Edit sx={{ fontSize: 14 }} />}
                        color="warning"
                        onClick={() => handleOpenDialogy()}
                    >
                        Edit
                    </Button>
                }
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 40,
                        bottom: 0,
                        right: 0,
                        left: 0,
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        sx={{
                            background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor6})`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Inventory
                                sx={{ fontSize: 55, color: colors.primary }}
                            />
                            <Box>
                                <Typography
                                    sx={{
                                        color: colors.primary,
                                        fontWeight: 'bold',
                                    }}
                                >
                                    {branchInfo?.branch_name}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: colors.primary,
                                    }}
                                >
                                    <LocationOn sx={{ fontSize: 16 }} />
                                    {branchInfo?.branch_location}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            {branchInfo?.branch_status ? (
                                <Chip
                                    size="small"
                                    label="Active"
                                    color="success"
                                />
                            ) : (
                                <Chip
                                    size="small"
                                    label="Closed"
                                    color="error"
                                />
                            )}

                            <Typography sx={{ textAlign: 'center' }}>
                                {branchInfo?.branch_status ? (
                                    <Switch
                                        inputRef={inputRef}
                                        onChange={(e) => activateBranch(e)}
                                        size="small"
                                        defaultChecked
                                    />
                                ) : (
                                    <Switch
                                        inputRef={inputRef}
                                        onChange={(e) => activateBranch(e)}
                                        size="small"
                                        defaultChecked={false}
                                    />
                                )}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography
                            sx={{
                                fontSize: 35,
                                color: colors.primary,
                                fontWeight: 'bold',
                            }}
                        >
                            {numberWithCommas(totalSales)}
                            <sup
                                style={{
                                    fontSize: 14,
                                    color: colors.primary,
                                }}
                            >
                                TZS
                            </sup>
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                textAlign: 'center',
                                color: colors.primary,
                            }}
                        >
                            Total Sales
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor3})`,
                            p: 2,
                        }}
                    >
                        <Grid container spacing={4}>
                            <Grid item sm={6}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor2})`,
                                        p: 0.5,
                                        px: 1,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            color: colors.primary,
                                            p: 1,
                                            mr: 1,
                                            fontWeight: 'bold',
                                            borderRight: `1px solid ${colors.bgColor3}`,
                                        }}
                                    >
                                        {productsCount}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 'bold',
                                            color: colors.primary,
                                        }}
                                    >
                                        Products
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item sm={6}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor2})`,
                                        p: 0.5,
                                        px: 1,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            color: colors.primary,
                                            fontWeight: 'bold',
                                            p: 1,
                                            mr: 1,
                                            borderRight: `1px solid ${colors.bgColor3}`,
                                        }}
                                    >
                                        50
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 'bold',
                                            color: colors.primary,
                                        }}
                                    >
                                        Orders
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item sm={6}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor2})`,
                                        p: 0.5,
                                        px: 1,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            color: colors.primary,
                                            fontWeight: 'bold',
                                            p: 1,
                                            mr: 1,
                                            borderRight: `1px solid ${colors.bgColor3}`,
                                        }}
                                    >
                                        {productsCount - outStockCount}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 'bold',
                                            color: colors.primary,
                                        }}
                                    >
                                        In stock products
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item sm={6}>
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor2})`,
                                        p: 0.5,
                                        px: 1,
                                        borderRadius: 2,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            color: colors.primary,
                                            fontWeight: 'bold',
                                            p: 1,
                                            mr: 1,
                                            borderRight: `1px solid ${colors.bgColor3}`,
                                        }}
                                    >
                                        {outStockCount}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            fontWeight: 'bold',
                                            color: colors.primary,
                                        }}
                                    >
                                        Out stock products
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </CustomCard2>
        </>
    );
};

export default BranchDetailsSidebar;
